import React, { useEffect, useState, useRef } from "react";
import "./ChatBox.css";
// REMOVED AS PART OF 19.2 UPGRADE
//import Picker from "@emoji-mart/react";
//import data from "@emoji-mart/data";
import { jwtDecode } from "jwt-decode";
import { MentionsInput, Mention } from "react-mentions";
import axios from "axios";
import { Tooltip } from "@mui/material";

function ChatBox({ orderId, assignedUsers, allUsers = [], notes = [] }) {
  const chatBoxRef = useRef(null);
  //const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);

  // --- Sticky scroll state/refs ---
  const forceScrollRef = useRef(false); // force next auto-scroll (e.g., after YOU send)
  const [stickToBottom, setStickToBottom] = useState(true); // true when user is near bottom
  const [hasNewWhileReading, setHasNewWhileReading] = useState(false); // show button when new stuff arrives

  const [isMentioning, setIsMentioning] = useState(false);
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewFileURL, setPreviewFileURL] = useState(null);

  // Merge assignedUsers with full allUsers details
  const fullAssignedUsers = assignedUsers.map((u) => {
    const found = allUsers.find((x) => x.id === u.id);
    return found ? { ...u, ...found } : u;
  });

  // Prepare mention data: assigned first, then others
  const mentionData = [
    ...fullAssignedUsers.map((u) => ({
      id: String(u.id),
      display:
        u.first_name && u.last_name
          ? `${u.first_name} ${u.last_name}`
          : u.username || `User #${u.id}`,
    })),
    ...allUsers
      .filter((u) => !fullAssignedUsers.some((fu) => fu.id === u.id))
      .map((u) => ({
        id: String(u.id),
        display:
          u.first_name && u.last_name
            ? `${u.first_name} ${u.last_name}`
            : u.username || `User #${u.id}`,
      })),
  ];

  function formatMessageDate(timestamp) {
    const messageDate = new Date(timestamp);
    const now = new Date();

    const diffInMs = now - messageDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 3600 * 24));

    const time = messageDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (diffInDays === 0) return "Today " + time;
    if (diffInDays === 1) return "Yesterday " + time;

    const date = messageDate.toLocaleDateString();
    return date + " " + time;
  }

  // Decode JWT to get logged-in user ID
  function getUserId() {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.userId || decoded.id || decoded.sub || null;
    } catch {
      return null;
    }
  }
  const UserId = getUserId();

  const stripHtml = (html = "") => {
    const el = document.createElement("div");
    el.innerHTML = html;
    return (el.textContent || el.innerText || "").trim();
  };

  const items = React.useMemo(() => {
    const chatItems = (messages || [])
      .filter((m) => !String(m?.message || "").startsWith("SYS:"))
      .map((m) => ({
        kind: "chat",
        id: `chat-${m.id || m.createdAt}`,
        createdAt: m.createdAt,
        displayName:
          m.User?.id === UserId
            ? "You"
            : m.User?.first_name && m.User?.last_name
            ? `${m.User.first_name} ${m.User.last_name}`
            : "Unknown User",
        message: m.message || "",
        attachmentUrl: m.attachmentUrl || "",
      }));

    const noteItems = (notes || []).map((n) => ({
      kind: "note",
      id: `note-${n.sequence || n.id || Math.random().toString(36).slice(2)}`,
      createdAt: n.timestamp || n.createdAt,
      displayName: n.userName || "Customer Note",
      message: stripHtml(n.message || ""),
      rawHtml: n.message || "",
      action: n.action,
    }));

    return [...chatItems, ...noteItems].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
  }, [messages, notes, UserId]);

  // --- Helpers for sticky scroll ---
  const isNearBottom = (el, px = 24) =>
    el.scrollHeight - el.scrollTop - el.clientHeight <= px;

  const scrollToBottom = () => {
    const el = chatBoxRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const handleScroll = () => {
    const el = chatBoxRef.current;
    if (!el) return;
    const atBottom = isNearBottom(el);
    setStickToBottom(atBottom);
    if (atBottom) setHasNewWhileReading(false);
  };

  // Fetch messages when orderId changes
  useEffect(() => {
    const token = localStorage.getItem("accessToken");

    async function fetchMessages() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/chat/messages?orderId=${orderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        const next = Array.isArray(data) ? data : data.messages || [];
        setMessages((prev) => {
          const prevLast = prev[prev.length - 1];
          const nextLast = next[next.length - 1];
          const prevKey = prevLast?.id ?? prevLast?.createdAt ?? "";
          const nextKey = nextLast?.id ?? nextLast?.createdAt ?? "";
          const same =
            prev.length === next.length && String(prevKey) === String(nextKey);
          return same ? prev : next;
        });
      } catch (err) {
        console.error("Error fetching messages:", err);
        setMessages([]);
      }
    }

    if (orderId) {
      fetchMessages(); // initial
      const intervalId = setInterval(fetchMessages, 8000);
      return () => clearInterval(intervalId);
    }
  }, [orderId]);

  // Smart auto-scroll on new items
  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;

    const shouldScroll = stickToBottom || forceScrollRef.current;
    if (shouldScroll) {
      scrollToBottom();
      forceScrollRef.current = false;
    } else {
      // user is reading up; notify that something new arrived
      setHasNewWhileReading(true);
    }
  }, [items, stickToBottom]);

  // Send message (with optional file name)
  async function handleSend() {
    if (!input.trim() && !file) return;

    setIsProcessing(true);
    let attachmentUrl = "";

    if (file) {
      try {
        const { data: signed } = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/chat/upload-url`,
          {
            params: { fileName: file.name, fileType: file.type },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );

        await axios.put(signed.uploadURL, file, {
          headers: { "Content-Type": file.type },
          onUploadProgress: (evt) => {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadProgress(pct);
          },
        });
        setUploadProgress(100);

        attachmentUrl = `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${signed.key}`;
      } catch (err) {
        console.error("❌ S3 upload error:", err.response || err.message || err);
      }
    }

    try {
      const payload = {
        orderId,
        senderId: UserId,
        message: input.trim() || "\u200B",
        ...(attachmentUrl && { attachmentUrl }),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/chat/messages`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // Locally append and force-stick to bottom once
      setMessages((prev) => [...prev, res.data]);
      forceScrollRef.current = true;
    } catch (err) {
      console.error("❌ Error posting chat message:", err.response || err.message || err);
    } finally {
      setTimeout(() => {
        setUploadProgress(null);
        setIsProcessing(false);
        setFile(null);
        setFileName("");
        setInput("");
        setPreviewFileURL(null);
        const fileInput = document.getElementById("file-upload");
        if (fileInput) fileInput.value = "";
      }, 500);
    }
  }

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      const previewURL = URL.createObjectURL(selectedFile);
      setPreviewFileURL(previewURL);
    }
  };

  // Emoji insertion
  function addEmoji(emoji) {
    setInput((prev) => prev + emoji.native);
  }

  // Render message text, turning "@[Name](id)" into a pill
  function highlightText(text) {
    const pattern = /@\[(.+?)\]\((\d+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      parts.push(text.slice(lastIndex, match.index));
      parts.push(
        <span key={match.index} className="mention-pill">
          @{match[1]}
        </span>
      );
      lastIndex = pattern.lastIndex;
    }
    parts.push(text.slice(lastIndex));
    return parts;
  }

  return (
    <div className="chat-container" style={{ position: "relative" }}>
      <h3 className="chat-header" style={{ position: "relative" }}>
        Chat - Project #{orderId}

        {/* Top-left helper indicators with MUI Tooltips */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* Digest info icon */}
          <Tooltip
            title="Overview digest: chat activity is auto-saved to project overview 20 minutes of inactivity."
            arrow
            placement="right"
          >
            <button
              type="button"
              aria-label="How the digest works"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "default",
                boxShadow: "0 1px 4px rgba(0,0,0,.06)",
                marginLeft: 8,
              }}
            >
              <span className="material-icons" style={{ fontSize: 18 }}>
                info
              </span>
            </button>
          </Tooltip>

          {/* @ mention helper icon */}
          <Tooltip
            title="Tip: Use @ to tag a user. They’ll get an email so they don’t miss your message."
            arrow
            placement="right"
          >
            <button
              type="button"
              aria-label="Mention users"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "default",
                boxShadow: "0 1px 4px rgba(0,0,0,.06)",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                @
              </span>
            </button>
          </Tooltip>
        </div>
      </h3>

      <div className="chat-box" ref={chatBoxRef} onScroll={handleScroll}>
        {items.map((itm) => {
          const isNote = itm.kind === "note";
          const wrapperCls = isNote
            ? "is-note"
            : itm.displayName === "You"
            ? "you"
            : "other";
          return (
            <div key={itm.id} className={`chat-message-wrapper ${wrapperCls}`}>
              <div className={`chat-message ${wrapperCls}`}>
                {itm.displayName !== "You" && (
                  <div className="message-header">
                    <span className="chat-sender">
                      {isNote
                        ? `${itm.displayName || "Note"} (Note)`
                        : itm.displayName}
                    </span>
                  </div>
                )}

                <div className="chat-body">
                  {!isNote && itm.attachmentUrl && (
                    <div className="chat-file">
                      <a
                        href={itm.attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="chat-file-link"
                        title={itm.attachmentUrl.split("/").pop()}
                      >
                        📄Click to view or download
                      </a>
                    </div>
                  )}

                  <div className="chat-message-wrapper">
                    <div className="chat-message">
                      <div className="chat-text-time">
                        <div className="chat-text">
                          {isNote ? (
                            <span
                              dangerouslySetInnerHTML={{ __html: itm.rawHtml }}
                            />
                          ) : (
                            highlightText(itm.message)
                          )}
                        </div>
                        <div className="chat-time">
                          {formatMessageDate(itm.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Jump-to-latest floating button with arrow-down icon (MUI Tooltip) */}
      {hasNewWhileReading && !stickToBottom && (
        <Tooltip title="Jump to latest" arrow placement="left">
          <button
            aria-label="Jump to latest"
            onClick={() => {
              scrollToBottom();
              setHasNewWhileReading(false);
              setStickToBottom(true);
            }}
            style={{
              position: "absolute",
              right: 12,
              bottom: 122, // sit above the input area
              width: 40,
              height: 40,
              borderRadius: "999px",
              border: "1px solid #e5e7eb",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,.10)",
            }}
          >
            <span className="material-icons">arrow_downward</span>
          </button>
        </Tooltip>
      )}

      <div className="chat-input-section">
        {/* Show Upload Progress Bar */}
        {uploadProgress !== null && uploadProgress < 100 && (
          <div className="upload-progress-bar">
            <p className="upload-text">
              Uploading: {fileName} ({uploadProgress}%)
            </p>
            <div className="upload-bar-bg">
              <div
                className="upload-bar-fg"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Show Preview + Spinner when upload is complete but response not received */}
        {uploadProgress === 100 && isProcessing && previewFileURL && (
          <div className="preview-uploading-container">
            {file && file.type.startsWith("image/") ? (
              <>
                <img
                  src={previewFileURL}
                  alt="Preview"
                  className="preview-image"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                <div className="spinner-overlay">
                  <span className="spinner" />
                </div>
              </>
            ) : (
              <>
                <div className="file-icon-container">
                  <span>📄 {file?.name}</span>
                </div>
                <div className="spinner-overlay">
                  <span className="spinner" />
                </div>
              </>
            )}
          </div>
        )}

        <div className="input-container">
          <button
            className="icon-button"
            onClick={(e) => {
              e.stopPropagation();
              //setShowEmojiPicker((v) => !v);
            }}
          >
            😀
          </button>
          {/* REMOVED AS PART OF 19.2 UPGRADE */ }
          {/*showEmojiPicker && (
            <div className="emoji-picker" ref={emojiPickerRef}>
              <Picker data={data} onEmojiSelect={addEmoji} />
            </div>
          )*/}

          <label htmlFor="file-upload" className="icon-button" title="Attach">
            <span className="material-icons">attach_file</span>
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {fileName && (
            <div className="attached-file-chip">
              📎 {fileName}
              <span
                className="remove-chip"
                onClick={() => {
                  setFile(null);
                  setFileName("");
                }}
              >
                ❌
              </span>
            </div>
          )}

          <div className="message-input-wrapper">
            <MentionsInput
              value={input}
              onChange={(e, v) => {
                setInput(v);
              }}
              placeholder="Type a message..."
              className="mentions"
              inputRef={inputRef}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  setIsMentioning(true);
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  if (isMentioning) {
                    setIsMentioning(false); // complete the mention
                    return;
                  }
                  e.preventDefault();
                  // handleSend(); // uncomment if you want Enter to send
                }
              }}
            >
              <Mention
                trigger="@"
                data={mentionData}
                markup="@[__display__](__id__)"
                displayTransform={(id, display) => `@${display}`}
                mentionClassName="mentions__mention"
                onAdd={() => setIsMentioning(false)}
                renderSuggestion={(suggestion, search, highlighted) => (
                  <div
                    className={`mention-suggestion-item ${
                      fullAssignedUsers.some(
                        (u) => String(u.id) === suggestion.id
                      )
                        ? "assigned"
                        : ""
                    }`}
                  >
                    {highlighted}
                    {fullAssignedUsers.some(
                      (u) => String(u.id) === suggestion.id
                    ) && <span className="assigned-badge">Assigned</span>}
                  </div>
                )}
              />
            </MentionsInput>
          </div>

          <button className="send-button" onClick={handleSend}>
            <span className="material-icons">send</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatBox;
